import { useEffect, useState } from "react";
import { Loader2, Upload, FileText, Save, ShieldAlert, Trash2 } from "lucide-react";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/auth-context";

const STATUS_OPTIONS = [
  "draft",
  "in_progress",
  "summarized",
  "under_review",
  "info_requested",
  "attorney_interested",
  "attorney_contacted",
  "retained",
  "complete",
  "rejected",
  "closed",
];

const URGENCY_OPTIONS = ["low", "medium", "high", "critical"];
const VALUE_OPTIONS = ["low", "medium", "high"];

/**
 * Reusable case editor + document uploader for admin and law-firm roles.
 *
 * Props:
 *  - caseData: the case row from the list (must contain id; other fields used as initial values)
 *  - open / onOpenChange: dialog control
 *  - onSaved: (updatedCase) => void  — caller patches its local list
 *
 * The editor calls PUT /api/cases/{id}/edit which is gated to admin + law_firm
 * on the backend. Document uploads use POST /api/cases/{id}/upload-auth which
 * captures the uploader's id+role for the audit trail.
 */
export default function CaseEditDialog({ caseData, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({
    case_type: "",
    state: "",
    city: "",
    status: "",
    urgency_level: "",
    case_value_score: "",
    estimated_value_band: "",
    admin_notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [documents, setDocuments] = useState([]);

  useEffect(() => {
    if (!caseData) return;
    setForm({
      case_type: caseData.case_type || "",
      state: caseData.state || "",
      city: caseData.city || "",
      status: caseData.status || "",
      urgency_level: caseData.urgency_level || "",
      case_value_score: caseData.case_value_score || "",
      estimated_value_band: caseData.estimated_value_band || "",
      admin_notes: caseData.admin_notes || "",
    });
    setDocuments(caseData.documents || []);
  }, [caseData]);

  if (!caseData) return null;

  const setField = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      // Only send fields that actually changed to keep audit logs clean.
      const payload = {};
      Object.entries(form).forEach(([k, v]) => {
        if (v !== (caseData[k] ?? "")) payload[k] = v === "" ? null : v;
      });
      if (Object.keys(payload).length === 0) {
        toast.message("Nothing to save");
        return;
      }
      const { data } = await api.put(`/cases/${caseData.id}/edit`, payload);
      toast.success("Case updated");
      onSaved?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save changes");
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = ""; // allow re-uploading the same file
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/cases/${caseData.id}/upload-auth`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setDocuments((prev) => [...prev, data]);
      toast.success(`Uploaded ${data.original_filename}`);
      onSaved?.({ ...caseData, documents: [...documents, data] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDoc = async (doc) => {
    if (!window.confirm(`Delete ${doc.original_filename}?`)) return;
    try {
      await api.delete(`/cases/${caseData.id}/documents/${doc.id}`);
      const next = documents.filter((d) => d.id !== doc.id);
      setDocuments(next);
      toast.success("Document removed");
      onSaved?.({ ...caseData, documents: next });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete document");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="case-edit-dialog"
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl text-[#0A192F]">
            Edit case
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          <div>
            <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">Case type</Label>
            <Input
              data-testid="edit-case-type"
              className="mt-1"
              value={form.case_type}
              onChange={(e) => setField("case_type")(e.target.value)}
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">Status</Label>
            <Select value={form.status} onValueChange={setField("status")}>
              <SelectTrigger data-testid="edit-status" className="mt-1">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">State</Label>
            <Input
              data-testid="edit-state"
              className="mt-1"
              value={form.state}
              maxLength={2}
              onChange={(e) => setField("state")(e.target.value.toUpperCase())}
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">City</Label>
            <Input
              data-testid="edit-city"
              className="mt-1"
              value={form.city}
              onChange={(e) => setField("city")(e.target.value)}
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">Urgency</Label>
            <Select value={form.urgency_level} onValueChange={setField("urgency_level")}>
              <SelectTrigger data-testid="edit-urgency" className="mt-1">
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent>
                {URGENCY_OPTIONS.map((u) => (
                  <SelectItem key={u} value={u} className="capitalize">{u}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">Case value</Label>
            <Select value={form.case_value_score} onValueChange={setField("case_value_score")}>
              <SelectTrigger data-testid="edit-value" className="mt-1">
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent>
                {VALUE_OPTIONS.map((v) => (
                  <SelectItem key={v} value={v} className="capitalize">{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">
              Internal notes
            </Label>
            <Textarea
              data-testid="edit-admin-notes"
              className="mt-1 min-h-[100px]"
              placeholder="Add any internal context, follow-ups, or notes here…"
              value={form.admin_notes}
              onChange={(e) => setField("admin_notes")(e.target.value)}
            />
            <p className="mt-1 text-[11px] text-slate-500 flex items-center gap-1">
              <ShieldAlert className="w-3 h-3" /> Visible to admins and firm staff only — not clients.
            </p>
          </div>
        </div>

        <div className="mt-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs uppercase tracking-[0.18em] font-semibold text-[#0A192F]">
              Documents ({documents.length})
            </p>
            <label className="cursor-pointer">
              <input
                type="file"
                className="hidden"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.heic"
                onChange={handleUpload}
                disabled={uploading}
                data-testid="upload-document-input"
              />
              <span
                data-testid="upload-document-btn"
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#0A192F] text-white text-sm font-medium hover:bg-[#112A46] disabled:opacity-60"
              >
                {uploading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
                Upload document
              </span>
            </label>
          </div>
          {documents.length === 0 ? (
            <p className="text-sm text-slate-500 italic">No documents yet.</p>
          ) : (
            <ul className="space-y-1.5">
              {documents.map((d) => (
                <li
                  key={d.id}
                  data-testid={`doc-${d.id}`}
                  className="flex items-center gap-2 text-sm text-slate-700 p-2 rounded bg-slate-50 border border-slate-200"
                >
                  <FileText className="w-4 h-4 text-[#C5A059] shrink-0" />
                  <span className="truncate flex-1">{d.original_filename}</span>
                  {d.uploaded_by_email && (
                    <span className="text-xs text-slate-500 hidden sm:inline">
                      by {d.uploaded_by_email}
                    </span>
                  )}
                  <button
                    type="button"
                    data-testid={`delete-doc-${d.id}-btn`}
                    onClick={() => handleDeleteDoc(d)}
                    className="text-slate-400 hover:text-red-600 p-1 rounded transition"
                    aria-label="Delete document"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            data-testid="cancel-edit-btn"
            className="rounded-lg"
          >
            Close
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            data-testid="save-case-btn"
            className="rounded-lg bg-[#0A192F] hover:bg-[#112A46] text-white"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            {saving ? "" : "Save changes"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
